import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Badge,
  Stack,
  MenuItem,
  TablePagination,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Restaurant as RestaurantIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  LocalShipping as DeliveryIcon,
  AccessTime as TimeIcon,
  People as PeopleIcon,
  Discount as DiscountIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import subscriptionApi from "../../api/subscriptions";
import orderApi from "../../api/orders";
import useAuth from "../../hooks/useAuth";
import { format } from "date-fns";
import Layout from "../../components/layout/Layout";
import useSubscriptionRefresh from "../../hooks/useSubscriptionRefresh";
import { subscriptionCurrency, subscriptionDate as formatDate, subscriptionDaysRemaining, subscriptionDuration, subscriptionOrderItems } from "../../utils/subscriptionDisplay";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  SkipNext as SkipIcon,
  Cancel as CancelIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
} from "@mui/icons-material";

export interface ManualActivationAudit {
  isManual: boolean;
  verifiedWithGateway?: boolean;
  activatedBy?: {
    _id: string;
    name?: string;
    email?: string;
    role?: string;
  };
  activatedAt?: string;
  reason?: string;
  referenceId?: string;
}

export interface PaymentRecord {
  _id: string;
  amount: number;
  gstAmount?: number;
  discount?: number;
  discountCode?: string;
  paymentMethod: string;
  razorpayOrderId?: string;
  razorpayPaymentId?: string;
  cashfreeOrderId?: string;
  cashfreePaymentId?: string;
  status: "pending" | "success" | "failed";
  manualActivation?: ManualActivationAudit;
  createdAt: string;
  updatedAt: string;
}

interface Subscription {
  _id: string;
  uid: string;
  userId: {
    _id: string;
    name: string;
    email: string;
    phoneNumber: string;
  };
  planId: {
    _id: string;
    title: string;
    description: string;
    price: number;
  };
  status: "active" | "paused" | "cancelled" | "pending" | "completed";
  startDate: string;
  endDate: string;
  slot: string;
  programLength: string;
  customDuration?: number;
  planType?: string;
  drinkTier?: string;
  walletAmountUsed?: number;
  gstAmount?: number;
  portionSize?: "classic" | "light" | string;
  ordersRemaining: number;
  skipsRemaining: number;
  dietaryPreferences: string;
  allergens: string;
  deliveryDetails: {
    name: string;
    company: string;
    flatNumber: string;
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    postcode: string;
    city: string;
    state: string;
    deliveryInstructions: string[];
    additionalNote: string;
    addressType: string;
    phoneNumber: string;
  };
  total: number;
  totalDiscount: number;
  netAmount: number;
  discountCode: string;
  deliveryTime: string;
  peopleCount: number;
  paymentDetails?: PaymentRecord | string;
  addOns: any[];
  invoices: any[];
  modificationHistory: any[];
  activePause?: {
    startDate: string;
    endDate: string;
    ordersAffected: number;
    appendedOrderIds?: string[];
  } | null;
  pauseHistory?: Array<{
    startDate: string;
    endDate: string;
    pausedAt?: string;
    resumedAt?: string;
    ordersAffected: number;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface Order {
  _id: string;
  mealId: {
    _id: string;
    name: string;
    description: string;
    image: string;
  };
  meals?: Array<{ mealId?: { name?: string } | null }>;
  drinkId?: { name?: string } | null;
  drinks?: Array<{ drinkId?: { name?: string } | null }>;
  deliveryDate: string;
  status: string;
  createdAt: string;
  deliveryPhotoUrl?: string;
  deliveryMode?: string;
}

const statusColors: Record<
  string,
  "success" | "warning" | "error" | "default" | "info"
> = {
  active: "success",
  paused: "warning",
  cancelled: "error",
  pending: "info",
  completed: "default",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  paused: "Paused",
  cancelled: "Cancelled",
  pending: "Pending",
  completed: "Completed",
};

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const SubscriptionDetail: React.FC = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isSuperAdmin = admin?.role === "super-admin";

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [orderStatus, setOrderStatus] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [orderPage, setOrderPage] = useState(0);
  const [orderPageSize, setOrderPageSize] = useState(10);
  const request = useRef<AbortController | null>(null);
  const { revision, refresh, lastUpdated, setLastUpdated } = useSubscriptionRefresh();

  // Dialog states
  const [openExtendDialog, setOpenExtendDialog] = useState(false);
  const [extendDays, setExtendDays] = useState("");
  const [openSkipsDialog, setOpenSkipsDialog] = useState(false);
  const [skipsCount, setSkipsCount] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Pause/Resume admin states
  const [openPauseDialog, setOpenPauseDialog] = useState(false);
  const [pauseStartDate, setPauseStartDate] = useState("");
  const [pauseEndDate, setPauseEndDate] = useState("");
  const [openExtendPauseDialog, setOpenExtendPauseDialog] = useState(false);
  const [newPauseEndDate, setNewPauseEndDate] = useState("");

  // Verify Payment dialog state
  const [openVerifyDialog, setOpenVerifyDialog] = useState(false);

  // Manual Activate states
  const [openManualActivateDialog, setOpenManualActivateDialog] = useState(false);
  const [manualReason, setManualReason] = useState("");
  const [manualRefId, setManualRefId] = useState("");

  const fetchSubscriptionDetails = useCallback(async () => {
    if (!subscriptionId) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true);
    try {
      const data = await subscriptionApi.getSubscriptionDetails(subscriptionId, controller.signal);
      if (controller.signal.aborted) return;
      setError(null);
      setLastUpdated(new Date());
      setSubscription(data.subscription);
      setOrders(data.orders || []);
      setPayments(data.payments || []);
    } catch (err: any) {
      if (controller.signal.aborted) return;
      setError(err.response?.data?.message || "Error fetching subscription details");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [subscriptionId, setLastUpdated]);

  useEffect(() => {
    setSubscription(null);
    setOrders([]);
    setPayments([]);
    setOrderPage(0);
    setOrderStatus("");
    setOrderSearch("");
  }, [subscriptionId]);
  useEffect(() => {
    fetchSubscriptionDetails();
    return () => request.current?.abort();
  }, [fetchSubscriptionDetails, revision]);

  const handleVerifyPayment = async (paymentIdToVerify: string) => {
    setActionLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const res = await subscriptionApi.verifyPayment(subscriptionId!, paymentIdToVerify);
      setSuccessMessage(res.message || "Payment verified and subscription activated successfully!");
      fetchSubscriptionDetails();
    } catch (err: any) {
      setActionError(err.response?.data?.message || err.message || "Gateway payment verification failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualActivate = async () => {
    if (!manualReason || !manualReason.trim()) {
      alert("Please provide a reason for manual activation.");
      return;
    }

    setActionLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const res = await subscriptionApi.manualActivate(subscriptionId!, {
        paymentId: primaryPaymentId,
        reason: manualReason.trim(),
        referenceId: manualRefId.trim() || undefined,
      });
      setSuccessMessage(res.message || "Subscription manually activated successfully!");
      setOpenManualActivateDialog(false);
      setManualReason("");
      setManualRefId("");
      fetchSubscriptionDetails();
    } catch (err: any) {
      setActionError(err.response?.data?.message || err.message || "Manual activation failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendSubscription = async () => {
    if (!extendDays || isNaN(Number(extendDays)) || Number(extendDays) <= 0) {
      alert("Please enter a valid number of days");
      return;
    }

    setActionLoading(true);
    try {
      await subscriptionApi.extendSubscription(subscriptionId!, Number(extendDays));
      setOpenExtendDialog(false);
      setExtendDays("");
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error extending subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleIncreaseSkips = async () => {
    if (!skipsCount || isNaN(Number(skipsCount)) || Number(skipsCount) <= 0) {
      alert("Please enter a valid number");
      return;
    }

    setActionLoading(true);
    try {
      await subscriptionApi.increaseSkips(subscriptionId!, Number(skipsCount));
      setOpenSkipsDialog(false);
      setSkipsCount("");
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error increasing skips");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePauseSubscription = async () => {
    if (!pauseStartDate || !pauseEndDate) {
      alert("Please select both start and end dates");
      return;
    }

    setActionLoading(true);
    try {
      await subscriptionApi.pauseSubscription(
        subscriptionId!,
        new Date(pauseStartDate),
        new Date(pauseEndDate)
      );
      setOpenPauseDialog(false);
      setPauseStartDate("");
      setPauseEndDate("");
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error pausing subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResumeSubscription = async () => {
    if (!window.confirm("Are you sure you want to resume this subscription early? Unused paused days will be restored to the subscription length.")) {
      return;
    }

    setActionLoading(true);
    try {
      await subscriptionApi.resumeSubscription(subscriptionId!);
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error resuming subscription");
    } finally {
      setActionLoading(false);
    }
  };

  const handleExtendPauseSubscription = async () => {
    if (!newPauseEndDate) {
      alert("Please select a new end date");
      return;
    }

    setActionLoading(true);
    try {
      await subscriptionApi.extendPauseSubscription(subscriptionId!, new Date(newPauseEndDate));
      setOpenExtendPauseDialog(false);
      setNewPauseEndDate("");
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error extending pause");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipOrder = async (orderId: string) => {
    if (!window.confirm("Are you sure you want to skip this order?")) return;

    setActionLoading(true);
    try {
      await orderApi.skipOrder(orderId);
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error skipping order");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestoreOrder = async (orderId: string) => {
    if (!window.confirm("Restore this order and add 1 delivery to the subscription? Existing scheduled deliveries will remain. The subscription end date will be extended if needed.")) return;

    setActionLoading(true);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const result = await orderApi.restoreOrder(orderId);
      setSuccessMessage(result.message);
      await fetchSubscriptionDetails();
    } catch (err: any) {
      setActionError(err.response?.data?.message || "Error restoring order");
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelOrder = async (orderId: string) => {
    if (!window.confirm("Are you sure you want to cancel this order?")) return;

    setActionLoading(true);
    try {
      await orderApi.updateOrderStatus(orderId, "cancelled");
      fetchSubscriptionDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || "Error cancelling order");
    } finally {
      setActionLoading(false);
    }
  };



  if (loading && !subscription) {
    return (
      <Layout>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
          <Typography>Loading subscription details...</Typography>
        </Box>
      </Layout>
    );
  }

  if (!subscription) {
    return (
      <Layout>
        <Box sx={{ p: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {error || "Subscription not found"}
          </Alert>
          <Button
            component={Link}
            to="/subscriptions"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Subscriptions
          </Button>
        </Box>
      </Layout>
    );
  }

  const daysRemaining = subscriptionDaysRemaining(subscription.endDate);
  const nextOrder = orders.filter((order) => ["pending", "picked_up"].includes(order.status) && (subscriptionDaysRemaining(order.deliveryDate) ?? -1) >= 0)
    .sort((a, b) => new Date(a.deliveryDate).getTime() - new Date(b.deliveryDate).getTime())[0];
  const visibleOrders = orders.filter((order) => (!orderStatus || order.status === orderStatus) &&
    `${subscriptionOrderItems(order)} ${formatDate(order.deliveryDate)}`.toLowerCase().includes(orderSearch.toLowerCase()))
  const safeOrderPage = Math.min(orderPage, Math.max(0, Math.ceil(visibleOrders.length / orderPageSize) - 1));

  const paymentObj = typeof subscription.paymentDetails === "object" ? subscription.paymentDetails : null;
  const primaryPaymentId =
    paymentObj?._id ||
    (typeof subscription.paymentDetails === "string" ? subscription.paymentDetails : null) ||
    payments[0]?._id;
  const primaryPayment =
    paymentObj ||
    (primaryPaymentId ? payments.find((p) => p._id === primaryPaymentId) : null) ||
    payments[0] ||
    null;

  return (
    <Layout>
      <Box sx={{ p: { xs: 0, md: 1 }, minWidth: 0 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {/* Action Feedback Alerts */}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* Header */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={() => navigate("/subscriptions")}>
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5 }}>
                <Typography variant="h5" sx={{ overflowWrap: "anywhere" }}>
                  Subscription {subscription.uid}
                </Typography>
                <Chip
                  size="small"
                  label={subscription.portionSize?.toLowerCase() === "light" ? "Light" : "Classic"}
                  sx={{
                    fontWeight: 700,
                    bgcolor: subscription.portionSize?.toLowerCase() === "light" ? "#e0f2fe" : "#f3e8ff",
                    color: subscription.portionSize?.toLowerCase() === "light" ? "#0369a1" : "#7c3aed",
                    border: `1px solid ${subscription.portionSize?.toLowerCase() === "light" ? "#bae6fd" : "#ddd6fe"}`,
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {subscription.userId?.name || "Customer not recorded"} · {lastUpdated ? `Updated ${format(lastUpdated, "HH:mm:ss")}` : ""}
              </Typography>
            </Box>
          </Box>
          <Tooltip title="Refresh subscription"><IconButton aria-label="Refresh subscription" onClick={refresh} disabled={loading}><SyncIcon /></IconButton></Tooltip>

          {/* Super Admin Payment Actions */}
          {isSuperAdmin && primaryPaymentId && subscription.status !== "active" && primaryPayment?.status !== "success" && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                color="info"
                startIcon={<SyncIcon />}
                onClick={() => setOpenVerifyDialog(true)}
                disabled={actionLoading}
              >
                Verify Payment
              </Button>
              <Button
                variant="contained"
                color="warning"
                startIcon={<SecurityIcon />}
                onClick={() => setOpenManualActivateDialog(true)}
                disabled={actionLoading}
              >
                Manual Activate
              </Button>
            </Box>
          )}

          {subscription.status === "active" && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenExtendDialog(true)}
              >
                Extend Subscription
              </Button>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenSkipsDialog(true)}
              >
                Increase Skips
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<PauseIcon />}
                onClick={() => setOpenPauseDialog(true)}
              >
                Pause Subscription
              </Button>
            </Box>
          )}
          {subscription.status === "paused" && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<PlayArrowIcon />}
                onClick={handleResumeSubscription}
                disabled={actionLoading}
              >
                Resume Subscription
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<AddIcon />}
                onClick={() => setOpenExtendPauseDialog(true)}
                disabled={actionLoading}
              >
                Extend Pause
              </Button>
            </Box>
          )}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" }, gap: 2, py: 2, mb: 2, borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider" }}>
          {[
            ["Subscription", statusLabels[subscription.status]],
            ["Orders remaining", subscription.ordersRemaining ?? "Not recorded"],
            ["Next delivery", nextOrder ? formatDate(nextOrder.deliveryDate) : "None scheduled"],
            ["Payment", primaryPayment?.status || "No payment record"],
          ].map(([label, value]) => <Box key={String(label)} sx={{ minWidth: 0 }}><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="subtitle1" fontWeight={700} sx={{ overflowWrap: "anywhere" }}>{value}</Typography></Box>)}
        </Box>

        {/* Status Alert */}
        {daysRemaining !== null && daysRemaining < 7 && ["active", "paused"].includes(subscription.status) && (
          <Alert
            severity={daysRemaining < 0 ? "error" : "warning"}
            sx={{ mb: 3 }}
            icon={daysRemaining < 0 ? <WarningIcon /> : <ScheduleIcon />}
          >
            {daysRemaining < 0
              ? `Subscription ended ${Math.abs(daysRemaining)} days ago`
              : `Subscription ends in ${daysRemaining} days`
            }
          </Alert>
        )}

        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))", gap: 2, "& > *": { minWidth: 0 }, "& .MuiListItemText-primary": { overflowWrap: "anywhere" } }}>
          {/* Customer Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <PersonIcon />
                Customer Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={subscription.userId?.name || "N/A"}
                    secondary="Customer Name"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={subscription.userId?.email || "N/A"}
                    secondary="Email"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={subscription.userId?.phoneNumber || "N/A"}
                    secondary="Phone"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>

          {/* Plan Information */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <RestaurantIcon />
                Plan Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary={subscription.planId?.title || "N/A"}
                    secondary="Plan Name"
                  />
                </ListItem>
                {subscription.planType && <ListItem><ListItemText primary={subscription.planType.replace(/_/g, " ")} secondary="Plan type" /></ListItem>}
                {subscription.drinkTier && <ListItem><ListItemText primary={subscription.drinkTier.replace(/_/g, " ")} secondary="Drink tier" /></ListItem>}
                <ListItem>
                  <ListItemText
                    primary={subscriptionCurrency(subscription.planId?.price)}
                    secondary="Current catalogue price"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={subscriptionDuration(subscription.programLength, subscription.customDuration)}
                    secondary="Program Length"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={
                      <Chip
                        size="small"
                        label={subscription.portionSize?.toLowerCase() === "light" ? "Light" : "Classic"}
                        sx={{
                          fontWeight: 700,
                          bgcolor: subscription.portionSize?.toLowerCase() === "light" ? "#e0f2fe" : "#f3e8ff",
                          color: subscription.portionSize?.toLowerCase() === "light" ? "#0369a1" : "#7c3aed",
                          border: `1px solid ${subscription.portionSize?.toLowerCase() === "light" ? "#bae6fd" : "#ddd6fe"}`,
                        }}
                      />
                    }
                    secondary="Portion Size"
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary={subscription.ordersRemaining ?? "Not recorded"}
                    secondary="Orders Remaining"
                  />
                </ListItem>
                {subscription.skipsRemaining !== undefined && (
                  <ListItem>
                    <ListItemText
                      primary={subscription.skipsRemaining}
                      secondary="Skips Remaining"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>

          {/* Subscription Status */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <CheckCircleIcon />
                Subscription Status
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box>
                  <Chip
                    label={statusLabels[subscription.status] || subscription.status}
                    color={statusColors[subscription.status] || "default"}
                    size="medium"
                  />
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Start Date: {formatDate(subscription.startDate)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    End Date: {formatDate(subscription.endDate)}
                  </Typography>
                  {daysRemaining !== null && subscription.status === "active" && (
                    <Typography
                      variant="body2"
                      color={daysRemaining < 0 ? "error" : daysRemaining < 7 ? "warning" : "text.secondary"}
                    >
                      {daysRemaining < 0 ? `${Math.abs(daysRemaining)} days overdue` :
                        daysRemaining === 0 ? "Ends today" :
                          `${daysRemaining} days left`}
                    </Typography>
                  )}
                </Box>
                {subscription.status === "paused" && subscription.activePause && (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: "warning.lighter", borderRadius: 1, border: 1, borderColor: "warning.light" }}>
                    <Typography variant="subtitle2" color="warning.darker" sx={{ fontWeight: "bold" }}>
                      Active Pause Details
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pause Start: {formatDate(subscription.activePause.startDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pause End: {formatDate(subscription.activePause.endDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Orders Paused: {subscription.activePause.ordersAffected}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Slot: {subscription.slot || "N/A"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Delivery Time: {subscription.deliveryTime || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Pause History Card */}
          {subscription.pauseHistory && subscription.pauseHistory.length > 0 && (
            <Card sx={{ gridColumn: "1 / -1" }}>
              <CardContent>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <ScheduleIcon />
                  Pause & Vacation History
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Pause Start</TableCell>
                        <TableCell>Pause End</TableCell>
                        <TableCell>Paused At</TableCell>
                        <TableCell>Resumed At</TableCell>
                        <TableCell>Orders Affected</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subscription.pauseHistory.map((history: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell>{formatDate(history.startDate)}</TableCell>
                          <TableCell>{formatDate(history.endDate)}</TableCell>
                          <TableCell>{history.pausedAt ? formatDate(history.pausedAt) : "N/A"}</TableCell>
                          <TableCell>{history.resumedAt ? formatDate(history.resumedAt) : "N/A"}</TableCell>
                          <TableCell>{history.ordersAffected}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Payment Information */}
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <PaymentIcon />
                  Payment Information
                </Typography>
                {primaryPayment?.status && (
                  <Chip
                    size="small"
                    label={primaryPayment.status.toUpperCase()}
                    color={
                      primaryPayment.status === "success"
                        ? "success"
                        : primaryPayment.status === "pending"
                        ? "warning"
                        : "error"
                    }
                  />
                )}
              </Box>

              <List dense>
                {primaryPaymentId && (
                  <ListItem>
                    <ListItemText
                      primary={primaryPaymentId}
                      secondary="Payment ID"
                      primaryTypographyProps={{ variant: "body2", fontFamily: "monospace" }}
                    />
                  </ListItem>
                )}
                {primaryPayment?.paymentMethod && (
                  <ListItem>
                    <ListItemText
                      primary={primaryPayment.paymentMethod.toUpperCase()}
                      secondary="Payment Method"
                    />
                  </ListItem>
                )}
                {(primaryPayment?.cashfreeOrderId || primaryPayment?.razorpayOrderId) && (
                  <ListItem>
                    <ListItemText
                      primary={primaryPayment.cashfreeOrderId || primaryPayment.razorpayOrderId}
                      secondary="Gateway Order ID"
                      primaryTypographyProps={{ variant: "body2", fontFamily: "monospace" }}
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText
                    primary={subscriptionCurrency(subscription.netAmount)}
                    secondary="Subscription net amount"
                  />
                </ListItem>
                {primaryPayment && <ListItem><ListItemText primary={subscriptionCurrency(primaryPayment.amount)} secondary="Payment transaction amount" /></ListItem>}
                {subscription.gstAmount != null && <ListItem><ListItemText primary={subscriptionCurrency(subscription.gstAmount)} secondary="GST" /></ListItem>}
                {subscription.walletAmountUsed != null && <ListItem><ListItemText primary={subscriptionCurrency(subscription.walletAmountUsed)} secondary="Wallet amount used" /></ListItem>}
                {subscription.totalDiscount > 0 && (
                  <ListItem>
                    <ListItemText
                      primary={`-${formatCurrency(subscription.totalDiscount)}`}
                      secondary="Discount Applied"
                      sx={{ "& .MuiListItemText-primary": { color: "success.main" } }}
                    />
                  </ListItem>
                )}
                {subscription.discountCode && (
                  <ListItem>
                    <ListItemText
                      primary={subscription.discountCode}
                      secondary="Discount Code"
                    />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemText
                    primary={subscription.peopleCount || "N/A"}
                    secondary="People Count"
                  />
                </ListItem>
              </List>

              {/* Audit Trail for Manual Activation or Gateway Verification */}
              {primaryPayment?.manualActivation && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: primaryPayment.manualActivation.isManual ? "warning.lighter" : "info.lighter",
                    border: 1,
                    borderColor: primaryPayment.manualActivation.isManual ? "warning.light" : "info.light",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                    {primaryPayment.manualActivation.isManual
                      ? "🛡️ Manually Activated by Super Admin"
                      : "✅ Verified via Gateway API"}
                  </Typography>
                  {primaryPayment.manualActivation.activatedBy && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Admin: {primaryPayment.manualActivation.activatedBy.name || primaryPayment.manualActivation.activatedBy.email} ({primaryPayment.manualActivation.activatedBy.role || "super-admin"})
                    </Typography>
                  )}
                  {primaryPayment.manualActivation.activatedAt && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Date: {formatDate(primaryPayment.manualActivation.activatedAt)}
                    </Typography>
                  )}
                  {primaryPayment.manualActivation.reason && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Reason: {primaryPayment.manualActivation.reason}
                    </Typography>
                  )}
                  {primaryPayment.manualActivation.referenceId && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Ref / UTR: {primaryPayment.manualActivation.referenceId}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Super Admin In-Card Quick Actions */}
              {isSuperAdmin && primaryPaymentId && subscription.status !== "active" && primaryPayment?.status !== "success" && (
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider", display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    color="info"
                    size="small"
                    startIcon={<SyncIcon />}
                    onClick={() => setOpenVerifyDialog(true)}
                    disabled={actionLoading}
                  >
                    Verify with Gateway
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<SecurityIcon />}
                    onClick={() => setOpenManualActivateDialog(true)}
                    disabled={actionLoading}
                  >
                    Manual Activate
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Delivery Information */}
          <Card sx={{ gridColumn: "1 / -1" }}>
            <CardContent>
              <Typography variant="h6" sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <DeliveryIcon />
                Delivery Information
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 2 }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={subscription.deliveryDetails?.address || "N/A"}
                      secondary={[subscription.deliveryDetails?.flatNumber, subscription.deliveryDetails?.company, "Address"].filter(Boolean).join(" · ")}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.city || "N/A"}
                      secondary="City"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.state || "N/A"}
                      secondary="State"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.postcode || "N/A"}
                      secondary="Postcode"
                    />
                  </ListItem>
                </List>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.name || "N/A"}
                      secondary="Delivery Name"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.phoneNumber || "N/A"}
                      secondary="Delivery Phone"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={subscription.deliveryDetails?.addressType || "N/A"}
                      secondary="Address Type"
                    />
                  </ListItem>
                  {subscription.deliveryDetails?.additionalNote && (
                    <ListItem>
                      <ListItemText
                        primary={subscription.deliveryDetails.additionalNote}
                        secondary="Additional Notes"
                      />
                    </ListItem>
                  )}
                </List>
              </Box>
              {subscription.deliveryDetails?.deliveryInstructions &&
                subscription.deliveryDetails.deliveryInstructions.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Delivery Instructions:
                    </Typography>
                    <List dense>
                      {subscription.deliveryDetails.deliveryInstructions.map((instruction, index) => (
                        <ListItem key={index}>
                          <ListItemText primary={instruction} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
            </CardContent>
          </Card>

          {/* Dietary Preferences */}
          {(subscription.dietaryPreferences || subscription.allergens) && (
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Dietary Preferences
                </Typography>
                <List dense>
                  {subscription.dietaryPreferences && (
                    <ListItem>
                      <ListItemText
                        primary={subscription.dietaryPreferences}
                        secondary="Dietary Preference"
                      />
                    </ListItem>
                  )}
                  {subscription.allergens && (
                    <ListItem>
                      <ListItemText
                        primary={subscription.allergens}
                        secondary="Allergens"
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          )}

          {/* Orders History */}
          <Card sx={{ gridColumn: "1 / -1" }}>
            <CardContent>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 2 }}>
                <Typography variant="h6">Delivery history ({visibleOrders.length})</Typography>
                <TextField size="small" label="Search items or delivery date" value={orderSearch} onChange={(e) => { setOrderSearch(e.target.value); setOrderPage(0); }} />
                <TextField select size="small" label="Order status" value={orderStatus} onChange={(e) => { setOrderStatus(e.target.value); setOrderPage(0); }} sx={{ minWidth: 160 }}>
                  <MenuItem value="">All statuses</MenuItem>
                  {["pending", "picked_up", "completed", "cancelled"].map((status) => <MenuItem key={status} value={status}>{status.replace(/_/g, " ")}</MenuItem>)}
                </TextField>
              </Box>
              {visibleOrders.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Items</TableCell>
                        <TableCell>Delivery Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleOrders.slice(safeOrderPage * orderPageSize, (safeOrderPage + 1) * orderPageSize).map((order) => (
                        <TableRow key={order._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {subscriptionOrderItems(order)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {order._id}
                            </Typography>
                          </TableCell>
                          <TableCell>{formatDate(order.deliveryDate)}</TableCell>
                          <TableCell>
                            <Stack spacing={0.5} alignItems="flex-start">
                              <Chip
                                label={order.status}
                                color={statusColors[order.status] || "default"}
                                size="small"
                              />
                              {order.status === "completed" && order.deliveryMode && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", fontWeight: 600 }}>
                                    {order.deliveryMode === "hand_delivery" ? "🤝 Hand" : "📦 Doorstep"}
                                  </Typography>
                                  {order.deliveryPhotoUrl && (
                                    <Button
                                      component="a"
                                      href={order.deliveryPhotoUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      size="small"
                                      variant="text"
                                      sx={{ p: 0, minWidth: 0, textTransform: "none", fontSize: "0.68rem", lineHeight: 1 }}
                                    >
                                      (View Photo)
                                    </Button>
                                  )}
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{formatDate(order.createdAt)}</TableCell>
                          <TableCell>
                            {isSuperAdmin && subscription.status === "active" && order.status === "cancelled" && (subscriptionDaysRemaining(order.deliveryDate) ?? -1) > 0 && (
                              <Button size="small" onClick={() => handleRestoreOrder(order._id)} disabled={actionLoading}>
                                Restore order
                              </Button>
                            )}
                            {order.status === "pending" && (
                              <Box sx={{ display: "flex", gap: 1 }}>
                                <Tooltip title="Skip Order">
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => handleSkipOrder(order._id)}
                                    disabled={actionLoading}
                                  >
                                    <SkipIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel Order">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleCancelOrder(order._id)}
                                    disabled={actionLoading}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No deliveries match.
                </Typography>
              )}
              <TablePagination component="div" count={visibleOrders.length} page={safeOrderPage} rowsPerPage={orderPageSize} onPageChange={(_, page) => setOrderPage(page)} onRowsPerPageChange={(e) => { setOrderPageSize(Number(e.target.value)); setOrderPage(0); }} rowsPerPageOptions={[10, 25, 50]} />
            </CardContent>
          </Card>
          <Box sx={{ gridColumn: "1 / -1", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Payment history ({payments.length})</Typography>
            <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Date</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell><TableCell>Method</TableCell><TableCell>Reference</TableCell></TableRow></TableHead><TableBody>
              {payments.map((payment) => <TableRow key={payment._id}><TableCell>{formatDate(payment.createdAt)}</TableCell><TableCell>{subscriptionCurrency(payment.amount)}</TableCell><TableCell>{payment.status}</TableCell><TableCell>{payment.paymentMethod || "Not recorded"}</TableCell><TableCell>{payment.cashfreePaymentId || payment.razorpayPaymentId || payment.cashfreeOrderId || payment.razorpayOrderId || payment._id}</TableCell></TableRow>)}
              {!payments.length && <TableRow><TableCell colSpan={5}>No payment transactions recorded.</TableCell></TableRow>}
            </TableBody></Table></TableContainer>
          </Box>
          <Box sx={{ gridColumn: "1 / -1", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Invoices ({subscription.invoices?.length || 0})</Typography>
            <TableContainer><Table size="small"><TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Date</TableCell><TableCell>Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead><TableBody>
              {(subscription.invoices || []).map((invoice) => <TableRow key={invoice._id}><TableCell>{invoice.uid || invoice._id}</TableCell><TableCell>{formatDate(invoice.invoiceDate)}</TableCell><TableCell>{subscriptionCurrency(invoice.amount)}</TableCell><TableCell>{invoice.status}</TableCell></TableRow>)}
              {!subscription.invoices?.length && <TableRow><TableCell colSpan={4}>No invoices recorded.</TableCell></TableRow>}
            </TableBody></Table></TableContainer>
          </Box>
          <Box sx={{ gridColumn: "1 / -1", py: 2, borderTop: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Subscription changes</Typography>
            {(subscription.modificationHistory || []).slice().reverse().map((entry, index) => <Box key={index} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}>
              <Typography variant="subtitle2">{entry.action || "Updated"} · {formatDate(entry.modifiedAt)}</Typography>
              {Object.entries(entry.changes || {}).map(([field, value]) => <Typography variant="body2" color="text.secondary" key={field} sx={{ overflowWrap: "anywhere" }}>{field}: {typeof value === "object" ? JSON.stringify(value) : String(value)}</Typography>)}
            </Box>)}
            {!subscription.modificationHistory?.length && <Typography variant="body2" color="text.secondary">No changes recorded.</Typography>}
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>Created: {formatDate(subscription.createdAt)} · Record updated: {formatDate(subscription.updatedAt)}</Typography>
          </Box>
        </Box>


      </Box>

      {/* Extend Subscription Dialog */}
      <Dialog open={openExtendDialog} onClose={() => setOpenExtendDialog(false)}>
        <DialogTitle>Extend Subscription</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Days"
            type="number"
            fullWidth
            variant="outlined"
            value={extendDays}
            onChange={(e) => setExtendDays(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExtendDialog(false)}>Cancel</Button>
          <Button onClick={handleExtendSubscription} variant="contained" disabled={actionLoading}>
            Extend
          </Button>
        </DialogActions>
      </Dialog>

      {/* Increase Skips Dialog */}
      <Dialog open={openSkipsDialog} onClose={() => setOpenSkipsDialog(false)}>
        <DialogTitle>Increase Skips</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Skips"
            type="number"
            fullWidth
            variant="outlined"
            value={skipsCount}
            onChange={(e) => setSkipsCount(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSkipsDialog(false)}>Cancel</Button>
          <Button onClick={handleIncreaseSkips} variant="contained" disabled={actionLoading}>
            Increase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Subscription Dialog */}
      <Dialog open={openPauseDialog} onClose={() => setOpenPauseDialog(false)}>
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Start Date"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={pauseStartDate}
            onChange={(e) => setPauseStartDate(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            margin="dense"
            label="End Date"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={pauseEndDate}
            onChange={(e) => setPauseEndDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPauseDialog(false)}>Cancel</Button>
          <Button onClick={handlePauseSubscription} variant="contained" color="warning" disabled={actionLoading}>
            Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Pause Dialog */}
      <Dialog open={openExtendPauseDialog} onClose={() => setOpenExtendPauseDialog(false)}>
        <DialogTitle>Extend Vacation Pause</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }} color="text.secondary">
            Choose a new pause end date. This must be after the current pause end date.
          </Typography>
          <TextField
            margin="dense"
            label="New End Date"
            type="date"
            fullWidth
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            value={newPauseEndDate}
            onChange={(e) => setNewPauseEndDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExtendPauseDialog(false)}>Cancel</Button>
          <Button onClick={handleExtendPauseSubscription} variant="contained" color="warning" disabled={actionLoading}>
            Extend Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* Verify Payment Confirmation Dialog (Super Admin) */}
      <Dialog
        open={openVerifyDialog}
        onClose={() => !actionLoading && setOpenVerifyDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SyncIcon color="info" />
          Verify Payment with Gateway
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            This will query the <strong>{primaryPayment?.paymentMethod?.toUpperCase() || "payment"}</strong> gateway in real-time for:
          </Typography>
          <Box sx={{ mb: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1, border: 1, borderColor: "grey.200" }}>
            <Typography variant="body2"><strong>Payment ID:</strong> {primaryPaymentId}</Typography>
            <Typography variant="body2">
              <strong>Gateway Order ID:</strong> {primaryPayment?.cashfreeOrderId || primaryPayment?.razorpayOrderId || "N/A"}
            </Typography>
            <Typography variant="body2"><strong>Amount:</strong> {subscriptionCurrency(subscription.netAmount)}</Typography>
          </Box>
          <Alert severity="info">
            If the gateway confirms the payment was captured/PAID, the subscription will be activated immediately and daily meal orders will be generated for the kitchen.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenVerifyDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (primaryPaymentId) {
                await handleVerifyPayment(primaryPaymentId);
                setOpenVerifyDialog(false);
              }
            }}
            variant="contained"
            color="info"
            disabled={actionLoading}
            startIcon={<SyncIcon />}
          >
            {actionLoading ? "Checking Gateway..." : "Confirm & Check Gateway"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Activate Confirmation Dialog (Super Admin) */}
      <Dialog
        open={openManualActivateDialog}
        onClose={() => !actionLoading && setOpenManualActivateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningIcon color="warning" />
          Manual Subscription Activation
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            <strong>Warning:</strong> Manually activating this subscription bypasses payment gateway verification.
            This action will immediately:
            <ul style={{ margin: "6px 0 0 16px", padding: 0 }}>
              <li>Mark the payment status as <strong>SUCCESS</strong></li>
              <li>Activate the subscription and calculate schedule</li>
              <li>Generate daily <strong>kitchen meal orders</strong></li>
              <li>Issue an official recognized <strong>GST Invoice</strong></li>
              <li>Disburse applicable <strong>referral wallet bonuses</strong></li>
              <li>Send automated <strong>welcome push notifications</strong></li>
            </ul>
          </Alert>

          <Box sx={{ mb: 2, p: 2, bgcolor: "grey.50", borderRadius: 1, border: 1, borderColor: "grey.200" }}>
            <Typography variant="body2"><strong>Subscription:</strong> {subscription.uid}</Typography>
            <Typography variant="body2">
              <strong>Customer:</strong> {subscription.userId?.name || "N/A"} ({subscription.userId?.email || subscription.userId?.phoneNumber || "N/A"})
            </Typography>
            <Typography variant="body2"><strong>Payment ID:</strong> {primaryPaymentId}</Typography>
            <Typography variant="body2"><strong>Net Amount:</strong> {subscriptionCurrency(subscription.netAmount)}</Typography>
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label="Activation Reason / Payment Source (Required)"
            placeholder="e.g., Direct Bank Transfer / Offline UPI / Cash Payment / VIP"
            fullWidth
            required
            variant="outlined"
            value={manualReason}
            onChange={(e) => setManualReason(e.target.value)}
            sx={{ mb: 2 }}
            helperText="Provide a clear, detailed reason for audit and financial logging"
          />

          <TextField
            margin="dense"
            label="Transaction Reference / UTR Number (Optional)"
            placeholder="e.g., UTR987654321012 or Gateway Txn ID"
            fullWidth
            variant="outlined"
            value={manualRefId}
            onChange={(e) => setManualRefId(e.target.value)}
            helperText="Bank transfer reference or offline transaction identifier"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenManualActivateDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleManualActivate}
            variant="contained"
            color="warning"
            disabled={actionLoading || !manualReason.trim()}
          >
            {actionLoading ? "Activating..." : "Confirm & Activate Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default SubscriptionDetail;
